/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   usePipelineMonitoring.ts
 *     Feature         :   206-pipeline-monitoring-redesign
 *     Description     :   React Query hooks for the two-tab monitoring section (Dashboard +
 *                         Job Activities + predictability), sourced from the universal-
 *                         logging-driven read API.
 *     Constitution    :   Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-30
 * =========================================================================================
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchDashboard, fetchActivities, fetchFilterOptions, fetchApps, fetchPredictability, fetchRunDetail,
  fetchMonitoringConfig, fetchCuration, fetchNewArrivals, fetchCurationAudit,
  patchCuration, bulkTriage, linkIdentity, unlinkIdentity,
} from '../services/pipelineMonitoringApi';
import type { FilterOptionsContext } from '../services/pipelineMonitoringApi';
import type { ActivitiesQuery, CurationPatch, ImportanceTier } from '@shared/types/pipelineMonitoring';

export const pipelineMonitoringKeys = {
  all: ['pipeline-monitoring'] as const,
  dashboard: (window: string) => [...pipelineMonitoringKeys.all, 'dashboard', window] as const,
  activities: (q: ActivitiesQuery) => [...pipelineMonitoringKeys.all, 'activities', q] as const,
  filterOptions: (ctx?: FilterOptionsContext) => [...pipelineMonitoringKeys.all, 'filter-options', ctx ?? {}] as const,
  apps: () => [...pipelineMonitoringKeys.all, 'apps'] as const,
  predictability: () => [...pipelineMonitoringKeys.all, 'predictability'] as const,
  runDetail: (runId: string | null) => [...pipelineMonitoringKeys.all, 'run-detail', runId] as const,
  curation: (showRetired: boolean) => [...pipelineMonitoringKeys.all, 'curation', showRetired] as const,
  newArrivals: () => [...pipelineMonitoringKeys.all, 'new-arrivals'] as const,
  audit: (appInstance: string) => [...pipelineMonitoringKeys.all, 'audit', appInstance] as const,
};

export function usePipelineDashboard(window: string) {
  return useQuery({
    queryKey: pipelineMonitoringKeys.dashboard(window),
    queryFn: () => fetchDashboard(window),
    refetchInterval: 30_000,
  });
}

export function usePipelineActivities(query: ActivitiesQuery) {
  return useQuery({
    queryKey: pipelineMonitoringKeys.activities(query),
    queryFn: () => fetchActivities(query),
    refetchInterval: 30_000,
  });
}

export function usePipelineFilterOptions(ctx: FilterOptionsContext = {}) {
  return useQuery({
    queryKey: pipelineMonitoringKeys.filterOptions(ctx),
    queryFn: () => fetchFilterOptions(ctx),
    staleTime: 60_000,
  });
}

export function usePipelineApps() {
  return useQuery({ queryKey: pipelineMonitoringKeys.apps(), queryFn: fetchApps, refetchInterval: 60_000 });
}

export function useMonitoringConfig() {
  return useQuery({
    queryKey: [...pipelineMonitoringKeys.all, 'config'],
    queryFn: fetchMonitoringConfig,
    staleTime: 5 * 60_000,
  });
}

export function usePipelinePredictability() {
  return useQuery({ queryKey: pipelineMonitoringKeys.predictability(), queryFn: fetchPredictability, refetchInterval: 30_000 });
}

export function usePipelineRunDetail(runId: string | null) {
  return useQuery({
    queryKey: pipelineMonitoringKeys.runDetail(runId),
    queryFn: () => fetchRunDetail(runId as string),
    enabled: Boolean(runId),
  });
}

// ── Spec 254 — curation (Management surface) ────────────────────────────────
export function usePipelineCuration(showRetired: boolean) {
  return useQuery({
    queryKey: pipelineMonitoringKeys.curation(showRetired),
    queryFn: () => fetchCuration(showRetired),
    refetchInterval: 30_000,
  });
}

export function useNewArrivals() {
  return useQuery({
    queryKey: pipelineMonitoringKeys.newArrivals(),
    queryFn: fetchNewArrivals,
    refetchInterval: 30_000,
  });
}

export function useCurationAudit(appInstance: string | null) {
  return useQuery({
    queryKey: pipelineMonitoringKeys.audit(appInstance ?? ''),
    queryFn: () => fetchCurationAudit(appInstance as string),
    enabled: Boolean(appInstance),
  });
}

/** Curation mutations — invalidate the whole section so the dashboard reflects within one refresh. */
export function useCurationMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: pipelineMonitoringKeys.all });
  return {
    patch: useMutation({ mutationFn: (v: { appInstance: string; patch: CurationPatch }) => patchCuration(v.appInstance, v.patch), onSuccess: invalidate }),
    bulk: useMutation({ mutationFn: (v: { appInstances: string[]; action: 'adopt' | 'retire'; tier?: ImportanceTier }) => bulkTriage(v.appInstances, v.action, v.tier), onSuccess: invalidate }),
    link: useMutation({ mutationFn: (v: { appInstance: string; prior: string }) => linkIdentity(v.appInstance, v.prior), onSuccess: invalidate }),
    unlink: useMutation({ mutationFn: (appInstance: string) => unlinkIdentity(appInstance), onSuccess: invalidate }),
  };
}
